import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { api } from '../api';
import { Key, Plus, Trash2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../contexts/AuthContext';

const SettingsPage = () => {
  const { user } = useAuth();
  const [keys, setKeys] = useState([]);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null); // Only shown once
  const [copied, setCopied] = useState(false);

  const fetchData = async () => {
    try {
      const keysRes = await api.get('/settings/keys');
      setKeys(keysRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleGenerate = async () => {
      try {
          const res = await api.post('/settings/keys', { label: newKeyLabel || 'My API Key' });
          setGeneratedKey(res.data.key);
          setKeys([...keys, res.data.info]);
          setNewKeyLabel('');
          setIsGenerateOpen(false); // Close input modal, show result modal? 
          // Actually better to show result in a dedicated modal or step.
      } catch (err) {
          toast.error("Failed to generate key");
      }
  };

  const handleRevoke = async (id) => {
      if (!window.confirm("Are you sure? Any integrations using this key will stop working.")) return;
      try {
          await api.delete(`/settings/keys/${id}`);
          setKeys(keys.map(k => k.id === id ? { ...k, status: 'revoked' } : k));
          toast.success("Key revoked");
      } catch (err) {
          toast.error("Failed to revoke");
      }
  };

  const copyKey = () => {
      navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied to clipboard");
  };

  return (
    <div className="h-full overflow-auto p-6 md:p-10 max-w-5xl mx-auto space-y-8 rounded-3xl lg-glass">
      <div className="flex flex-col gap-1">
        <h1 className="text-3xl font-bold tracking-tight text-white">Settings</h1>
        <p className="text-white/50">Manage your profile and developer access.</p>
      </div>

      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl mb-6">
          <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60">Profile</TabsTrigger>
          <TabsTrigger value="developer" className="rounded-lg data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60">Developer / API</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
            <Card className="lg-glass border-white/10 text-white">
                <CardHeader>
                    <CardTitle>Profile Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex items-center gap-4">
                        <div className="h-16 w-16 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400 text-xl font-bold border border-cyan-500/30 overflow-hidden">
                            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : user?.name?.charAt(0)}
                        </div>
                        <div>
                            <h3 className="font-medium text-lg">{user?.name}</h3>
                            <p className="text-white/50">{user?.email}</p>
                            <Badge variant="outline" className="mt-2 border-white/20 text-white/70 capitalize">{user?.role}</Badge>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </TabsContent>

        <TabsContent value="developer" className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-semibold text-white">API Keys</h2>
                    <p className="text-white/50 text-sm mt-1">Generate keys for Signal Tools and other integrations.</p>
                </div>
                <Button onClick={() => setIsGenerateOpen(true)} className="bg-cyan-500 text-black hover:bg-cyan-400 gap-2">
                    <Plus className="h-4 w-4" /> Generate New Key
                </Button>
            </div>

            {/* Generated Key Modal/Display */}
            {generatedKey && (
                <Card className="bg-green-500/10 border-green-500/20 text-white mb-6">
                    <CardHeader>
                        <CardTitle className="text-green-400 text-base">New API Key Generated</CardTitle>
                        <CardDescription className="text-white/60">
                            Please copy this key now. You will not be able to see it again.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex gap-2">
                            <code className="flex-1 bg-black/40 p-3 rounded-lg font-mono text-sm break-all text-green-300 border border-green-500/20">
                                {generatedKey}
                            </code>
                            <Button onClick={copyKey} size="icon" variant="ghost" className="text-white/50 hover:text-white">
                                {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
                            </Button>
                        </div>
                        <Button 
                            variant="ghost" 
                            className="mt-4 w-full text-white/40 hover:text-white"
                            onClick={() => setGeneratedKey(null)}
                        >
                            I have saved this key
                        </Button>
                    </CardContent>
                </Card>
            )}

            <div className="space-y-4">
                {keys.map((key) => (
                    <Card key={key.id} className="lg-glass border-white/10 text-white">
                        <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="h-10 w-10 rounded-full bg-purple-500/10 flex items-center justify-center border border-purple-500/20">
                                    <Key className="h-5 w-5 text-purple-400" />
                                </div>
                                <div>
                                    <h4 className="font-medium text-white">{key.label}</h4>
                                    <div className="flex items-center gap-2 mt-1">
                                        <code className="text-xs text-white/40 font-mono bg-white/5 px-1.5 py-0.5 rounded">{key.prefix}</code>
                                        <span className="text-xs text-white/30">• Created {new Date(key.created_at).toLocaleDateString()}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                {key.status === 'active' ? (
                                    <Badge variant="outline" className="border-green-500/20 text-green-400 bg-green-500/5">Active</Badge>
                                ) : (
                                    <Badge variant="destructive" className="opacity-50">Revoked</Badge>
                                )}
                                {key.status === 'active' && (
                                    <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        onClick={() => handleRevoke(key.id)}
                                        className="text-white/30 hover:text-red-400 hover:bg-red-500/10"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ))}
                
                {keys.length === 0 && !generatedKey && (
                    <div className="text-center py-12 text-white/30 border border-dashed border-white/10 rounded-xl">
                        No API keys generated yet.
                    </div>
                )}
            </div>
        </TabsContent>
      </Tabs>

      <Dialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen}>
        <DialogContent className="lg-glass-strong border-white/10 text-white">
            <DialogHeader>
                <DialogTitle>Create API Key</DialogTitle>
                <DialogDescription>Give your key a name to identify it later (e.g., "Signal Tools").</DialogDescription>
            </DialogHeader>
            <div className="py-4">
                <Label>Key Label</Label>
                <Input 
                    value={newKeyLabel} 
                    onChange={(e) => setNewKeyLabel(e.target.value)}
                    className="bg-black/20 border-white/10 text-white mt-2" 
                    placeholder="e.g. Signal Tools Integration"
                />
            </div>
            <DialogFooter>
                <Button variant="ghost" onClick={() => setIsGenerateOpen(false)}>Cancel</Button>
                <Button onClick={handleGenerate} className="bg-cyan-500 text-black hover:bg-cyan-400">Generate</Button>
            </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
